import type { Dispatch, SetStateAction } from "react";
import type { UseFormReturn } from "react-hook-form";
import { toast } from "react-hot-toast";

import type { AsyncAvData } from "@ui/hooks/useSessionAsyncAv";
import { useTranslations } from "@ui/providers/useTranslations";
import { getBackendErrorCode, getBackendErrorMessage } from "@ui/utils/backendError";

import { useConfig } from "@jti/app-context/useConfig";
import type {
  AgeVerificationScriptConfigItem,
  VerifyProviderError,
  VerifyResponse,
  VerifyVariables,
} from "@jti/fe-common";
import {
  getAgeVerificationUuid,
  prepareAddressForAv,
  removeAgeVerificationUuid,
  translateBackendErrorCode,
} from "@jti/fe-common";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import type { AvFormValues } from "../interfaces/AvFormValues";
import type { AvUserDataType } from "../interfaces/AvUserDataType";
import { resolveAgeEstimationResult } from "../utils/ageEstimationPolicy";

import type { AgeVerificationMethodType } from "./useAvMethods";

interface UseAvVerificationRequestsProps {
  verify: (variables: VerifyVariables) => Promise<VerifyResponse>;
  getAsyncMethodConfig: (methodName?: string) => AgeVerificationScriptConfigItem | undefined;
  form: UseFormReturn<AvFormValues>;
  userData: Partial<AvUserDataType>;
  selectedMethod: string;
  isOptional?: boolean;
  clearWidgetAvData: () => void;
  onSuccess?: (avpUuid?: string) => void;
  onFail?: (message?: string) => void;
  setAsyncPendingPopupOpened: Dispatch<SetStateAction<boolean>>;
  setIsValidationError: Dispatch<SetStateAction<boolean>>;
  isAgeEstimationFlowEnabled?: boolean;
  allAvailableMethods?: AgeVerificationMethodType[];
  onAgeEstimationResponse?: () => void;
  onFallbackRequired?: () => void;
}

/**
 * Owns the calls to the AV `verify` endpoint (sync, async and previous-uuid
 * checks), the payload building for each, and the shared handling of a
 * verification result (success/fail notification and popup state updates).
 * Behavior mirrors the original ValidationMethods implementation.
 */
export function useAvVerificationRequests({
  verify,
  getAsyncMethodConfig,
  form,
  userData,
  selectedMethod,
  isOptional,
  clearWidgetAvData,
  onSuccess,
  onFail,
  setAsyncPendingPopupOpened,
  setIsValidationError,
  isAgeEstimationFlowEnabled,
  allAvailableMethods = [],
  onAgeEstimationResponse,
  onFallbackRequired,
}: UseAvVerificationRequestsProps) {
  const config = useConfig();
  const { __, dictionary } = useTranslations();

  const isFormFilled = () => Object.values(form.getValues()?.avpValues || {}).some(Boolean);

  const onAgeVerificationFail = (message?: string) => {
    removeAgeVerificationUuid();
    setIsValidationError(true);
    toast.error(message || __("auth.age_verification.result.message_fail"));
    onFail?.(message);
  };

  const onAgeVerification = async (avpUuid: string, is_verified: boolean, error?: VerifyProviderError) => {
    if (avpUuid && is_verified) {
      removeAgeVerificationUuid();
      clearWidgetAvData();
      onSuccess?.(avpUuid);
    } else if ((isOptional && isFormFilled()) || (!isOptional && !is_verified)) {
      onAgeVerificationFail(translateBackendErrorCode(error?.error_code, error?.message, dictionary));
    }
  };

  const asyncAgeVerify = async ({
    ageVerificationMethodCode,
    dateOfBirth = "",
    email,
    firstName = "",
    lastName = "",
    request_uuid,
    workflowName,
    workflowDefinitionId,
  }: AsyncAvData) => {
    try {
      const {
        uuid: avpUuid,
        is_verified,
        verification_performed,
        status_from_provider,
        error,
      } = await verify({
        user_email: email,
        country_code: config.ISO_COUNTRY_CODE,
        method_code: ageVerificationMethodCode,
        fields: {
          firstname: firstName,
          lastname: lastName,
          date_of_birth: dateOfBirth,
          request_uuid,
          workflow_name: workflowName,
          workflow_definition_id: workflowDefinitionId,
        },
      });

      const ageEstimationResult = isAgeEstimationFlowEnabled
        ? resolveAgeEstimationResult({
            availableMethods: allAvailableMethods,
            isVerified: is_verified,
            methodCode: ageVerificationMethodCode,
            verificationPerformed: verification_performed,
          })
        : null;

      if (ageEstimationResult) {
        onAgeEstimationResponse?.();
      }

      if (ageEstimationResult === "pending") {
        setAsyncPendingPopupOpened(true);
        return;
      }
      if (ageEstimationResult === "passed") {
        setAsyncPendingPopupOpened(false);
        onAgeVerification(avpUuid, true);
        return;
      }
      if (ageEstimationResult === "fallback") {
        onFallbackRequired?.();
        return;
      }
      if (ageEstimationResult === "failed") {
        onAgeVerificationFail(translateBackendErrorCode(error?.error_code, error?.message, dictionary));
        return;
      }

      if (status_from_provider === "pending") {
        setAsyncPendingPopupOpened(true);
        return;
      }

      if (status_from_provider === "failed") {
        onAgeVerificationFail(
          translateBackendErrorCode(getBackendErrorCode(error), getBackendErrorMessage(error), dictionary)
        );
        return;
      }

      if (status_from_provider === "success") {
        setAsyncPendingPopupOpened(false);
      }
      onAgeVerification(avpUuid, is_verified);
    } catch (error) {
      onAgeVerificationFail(
        translateBackendErrorCode(getBackendErrorCode(error), getBackendErrorMessage(error), dictionary)
      );
    }
  };

  const verifyAge = async (formValues: AvFormValues, verificationMethodCode: string) => {
    try {
      const { firstname, lastname, dateOfBirth, phone, email, addresses } = userData;

      const {
        uuid: avpUuid,
        is_verified,
        error,
      } = await verify({
        user_email: email,
        country_code: config.ISO_COUNTRY_CODE,
        method_code: verificationMethodCode,
        fields: {
          ...formValues.avpValues,
          ...(userData?.additionalData || {}),
          firstname,
          lastname,
          date_of_birth: dateOfBirth,
          phone,
          ...prepareAddressForAv(addresses),
        },
      });

      if (is_verified) {
        dispatchTrackingEvent(TrackingEvents.TrackingHardAgeVerificationSuccess, {
          hav_method: verificationMethodCode,
        });
      }

      onAgeVerification(avpUuid, is_verified, error);
    } catch (error) {
      const errorMessage = translateBackendErrorCode(
        getBackendErrorCode(error),
        getBackendErrorMessage(error),
        dictionary
      );

      onAgeVerificationFail(errorMessage);
    }
  };

  // additional verification for async methods - motivated by veriff issue with long response time
  const handleCheckPrevAsyncVerification = async () => {
    const uuidFromCookie = getAgeVerificationUuid();
    const uuidFromForm = form.getValues("age_verification_uuid");
    const avUuid = uuidFromCookie || uuidFromForm;

    if (!avUuid) {
      return false;
    }

    try {
      const { is_verified, status_from_provider } = await verify({
        user_email: userData.email,
        country_code: config.ISO_COUNTRY_CODE,
        method_code: selectedMethod,
        fields: {
          firstname: userData.firstname,
          lastname: userData.lastname,
          date_of_birth: userData.dateOfBirth,
          request_uuid: avUuid,
          workflow_name: getAsyncMethodConfig(selectedMethod)?.workflowName,
          workflow_definition_id: getAsyncMethodConfig(selectedMethod)?.workflowDefinitionId,
        },
      });

      if (status_from_provider === "success" && is_verified) {
        toast.success(__("auth.age_verification.result.message_success.prev_uuid_verification"));
        onAgeVerification(avUuid, is_verified);
        return true;
      }
    } catch {
      removeAgeVerificationUuid();
      return false;
    }

    return false;
  };

  return {
    asyncAgeVerify,
    verifyAge,
    handleCheckPrevAsyncVerification,
    onAgeVerificationFail,
    isFormFilled,
  };
}
